import { useState } from "react";
import { motion } from "framer-motion";
import { HandHeart, Send, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const PRAYER_IMG = "https://images.unsplash.com/photo-1639254241852-1e9062106f39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxibGFjayUyMHBlb3BsZSUyMGNodXJjaCUyMHdvcnNoaXAlMjBwcmF5ZXIlMjBoYW5kcyUyMHJhaXNlZHxlbnwwfHx8fDE3NzYwODg2NTN8MA&ixlib=rb-4.1.0&q=85";
const WHATSAPP_NUMBER = "447738998165";

export default function PrayerRequest() {
  const [form, setForm] = useState({ name: "", request: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const name = form.name.trim() || "A member";
    const text = `Prayer Request from ${name}:\n\n${form.request}`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  return (
    <section id="prayer" data-testid="prayer-section" className="py-24 px-6 bg-navy relative overflow-hidden">
      <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-cgreen/10 blur-3xl" />
      <div className="absolute bottom-10 right-10 w-56 h-56 rounded-full bg-crimson/10 blur-3xl" />

      <div className="max-w-7xl mx-auto relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 text-white text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <HandHeart className="w-3.5 h-3.5" />
            Prayer
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
            Submit a Prayer Request
          </h2>
          <p className="font-body text-white text-base max-w-lg mx-auto">
            Share your prayer needs with us. Our church family will stand with you in prayer.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto items-center">
          <motion.div
            className="hidden lg:block rounded-3xl overflow-hidden shadow-2xl"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img src={PRAYER_IMG} alt="Prayer" className="w-full h-[480px] object-cover" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 space-y-5"
              data-testid="prayer-form"
            >
              <div className="space-y-2">
                <Label className="font-body text-white/80 text-sm">Your Name (optional)</Label>
                <Input
                  data-testid="prayer-name-input"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl bg-white/10 border-white/10 text-white placeholder:text-white/30 font-body"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body text-white/80 text-sm">Your Prayer Request</Label>
                <Textarea
                  data-testid="prayer-request-input"
                  required
                  rows={5}
                  placeholder="Share your prayer request..."
                  value={form.request}
                  onChange={(e) => setForm({ ...form, request: e.target.value })}
                  className="rounded-xl bg-white/10 border-white/10 text-white placeholder:text-white/30 font-body resize-none"
                />
              </div>

              <Button
                type="submit"
                data-testid="prayer-submit-btn"
                className="w-full rounded-full bg-cgreen hover:bg-cgreen/90 text-white py-6 font-body font-bold text-base shadow-lg shadow-cgreen/30 transition-all hover:-translate-y-0.5"
              >
                <Send className="w-4 h-4 mr-2" /> Send via WhatsApp
              </Button>

              <div className="text-center pt-2">
                <a href="tel:07738998165" className="flex items-center justify-center gap-2 text-white/60 font-body text-sm hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> Or call Pastor Samson: 07738998165
                </a>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
